/**
 * Centralized undo/redo arbiter (docs/undo_redo_incident_20260922.md §5.4).
 *
 * Exactly one `window` keydown listener (capture phase) exists in the whole app —
 * this module's — instead of every surface (drawing overlay, note editor, notebook
 * canvas, ...) independently listening for Ctrl+Z and guessing whether it should act.
 * A surface registers a scope on mount and calls `activate()`/`deactivate()` as it
 * becomes/stops being the thing the user is interacting with; "mounted" is NOT
 * "active" — a hidden/backgrounded component must not win undo just by existing.
 *
 * Ownership resolution: among scopes that are both registered-and-activated AND pass
 * their own `isActive()` check right now, the one activated most recently (topmost of
 * the interaction stack) wins — not a hardcoded priority number, so nesting (e.g. a
 * color picker opened from inside a thumbnail editor) resolves correctly without
 * hand-tuning every new surface's priority.
 */

export interface UndoScope {
    id: string;
    /** Extra liveness check beyond "registered and activated" — e.g. a modal that stays
     * mounted while hidden should return false here while hidden. Defaults to true. */
    isActive?: () => boolean;
    /** True only for a scope whose OWN native <input>/<textarea>/contentEditable should
     * still receive Ctrl+Z (e.g. the note-content editor). Everything else defers to
     * the browser's native undo when the event target is a plain editable field with
     * no registered scope of its own. Defaults to false. */
    allowNativeEditableTargets?: boolean;
    undo: () => void;
    redo: () => void;
    getUndoLabel?: () => string | null;
    getRedoLabel?: () => string | null;
}

interface RegisteredScope extends UndoScope {
    activated: boolean;
    activationOrder: number;
}

let scopes: RegisteredScope[] = [];
let activationCounter = 0;
let listenerInstalled = false;
let toastFn: ((message: string) => void) | null = null;

/** App.tsx calls this once with its existing showToast, so the arbiter can surface
 * "Undid: ..." / "Redid: ..." feedback (§5.6) without every scope needing its own
 * toast plumbing. */
export function setUndoArbiterToast(fn: ((message: string) => void) | null) {
    toastFn = fn;
}

function isNativeEditableTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable === true;
}

function pickActiveScope(): RegisteredScope | undefined {
    const live = scopes.filter(s => s.activated && (s.isActive ? s.isActive() : true));
    if (live.length === 0) return undefined;
    return live.reduce((best, s) => (s.activationOrder > best.activationOrder ? s : best));
}

function handleKeyDown(e: KeyboardEvent) {
    if (e.isComposing) return;
    // Browser autofill dispatches keydown events without a `key`.
    if (typeof e.key !== 'string') return;
    const key = e.key.toLowerCase();
    const isZ = key === 'z';
    const isY = key === 'y';
    const isUndo = (e.ctrlKey || e.metaKey) && isZ && !e.shiftKey;
    const isRedo = ((e.ctrlKey || e.metaKey) && isZ && e.shiftKey) || (e.ctrlKey && isY);
    if (!isUndo && !isRedo) return;

    const scope = pickActiveScope();
    if (!scope) return; // nothing has claimed undo right now — let the browser do its default thing

    if (isNativeEditableTarget(e.target) && !scope.allowNativeEditableTargets) {
        // The winning scope has no opinion about native editable fields (e.g. the
        // notebook-canvas fallback) — don't steal Ctrl+Z from the browser's own text
        // undo in some unrelated input/textarea/contentEditable.
        return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (isUndo) {
        const label = scope.getUndoLabel?.();
        scope.undo();
        if (label && toastFn) toastFn(label);
    } else {
        const label = scope.getRedoLabel?.();
        scope.redo();
        if (label && toastFn) toastFn(label);
    }
}

function ensureListener() {
    if (listenerInstalled || typeof window === 'undefined') return;
    window.addEventListener('keydown', handleKeyDown, true);
    listenerInstalled = true;
}

export interface UndoScopeHandle {
    activate: () => void;
    deactivate: () => void;
    unregister: () => void;
}

export function registerUndoScope(scope: UndoScope): UndoScopeHandle {
    ensureListener();
    const reg: RegisteredScope = { ...scope, activated: false, activationOrder: 0 };
    scopes.push(reg);
    return {
        activate: () => { reg.activated = true; reg.activationOrder = ++activationCounter; },
        deactivate: () => { reg.activated = false; },
        unregister: () => { scopes = scopes.filter(s => s !== reg); },
    };
}
